import * as THREE from 'three';
import type { SwingPathMetrics, SwingPathSample } from '../input/ShotIntent';

export type ContactQuality =
  'Pure' | 'Solid' | 'Slight Mishit' | 'Thin' | 'Fat' | 'Topped' | 'Heel' | 'Toe' | 'Severe Mishit';

export interface SwingMetrics {
  readonly power: number;
  readonly overswingRisk: number;
  readonly backswingDuration: number;
  readonly downswingDuration: number;
  readonly tempoRatio: number;
  readonly tempoScore: number;
  readonly pathDegrees: number;
  readonly faceDegrees: number;
  readonly faceToPathDegrees: number;
  readonly impactOffset: number;
  readonly followThroughDegrees: number;
  readonly strike: ContactQuality;
  readonly quality: number;
}

function segmentAngle(a: SwingPathSample, b: SwingPathSample): number {
  return THREE.MathUtils.radToDeg(Math.atan2(b.x - a.x, a.y - b.y));
}

function averageAngle(samples: readonly SwingPathSample[]): number {
  if (samples.length < 2) return 0;
  return segmentAngle(samples[0], samples[samples.length - 1]);
}

export function analyzeSwing(path: SwingPathMetrics, power: number): SwingMetrics {
  const samples = path.samples;
  const origin = samples[0];
  const transitionIndex = samples.reduce(
    (best, p, i) => (origin && p.y - origin.y > samples[best].y - origin.y ? i : best),
    0,
  );
  const impactIndex = path.impactPoint
    ? samples.findIndex(
        (p, index) =>
          index > transitionIndex && p.x === path.impactPoint?.x && p.y === path.impactPoint?.y,
      )
    : Math.max(transitionIndex + 1, Math.floor(samples.length * 0.72));
  const backswing = samples.slice(0, Math.max(transitionIndex + 1, 1));
  const downswing = samples.slice(transitionIndex, Math.max(impactIndex + 1, transitionIndex + 2));
  const follow = samples.slice(Math.max(impactIndex, 0));
  const backswingDuration = Math.max(
    0.001,
    (backswing.at(-1)?.timeSeconds ?? 0) - (backswing[0]?.timeSeconds ?? 0),
  );
  const downswingDuration = Math.max(
    0.001,
    (downswing.at(-1)?.timeSeconds ?? 0) - (downswing[0]?.timeSeconds ?? 0),
  );
  const tempoRatio = backswingDuration / downswingDuration;
  const tempoScore = THREE.MathUtils.clamp(1 - Math.abs(tempoRatio - 3) / 3.5, 0.35, 1);
  const pathDegrees = averageAngle(downswing);
  const followThroughDegrees = averageAngle(follow);
  const impactOffset =
    origin && path.impactPoint
      ? (path.impactPoint.x - origin.x) / Math.max(80, path.maxBackswingPixels)
      : 0;
  const faceDegrees = THREE.MathUtils.clamp(
    pathDegrees * 0.35 + followThroughDegrees * 0.45 + impactOffset * 10,
    -18,
    18,
  );
  const faceToPathDegrees = faceDegrees - pathDegrees;
  const overswingRisk = THREE.MathUtils.clamp((power - 96) / 18, 0, 1);
  const absImpact = Math.abs(impactOffset);
  const tempoPenalty = 1 - tempoScore;
  let strike: ContactQuality = 'Solid';
  if (power < 8 || tempoScore < 0.42 || absImpact > 0.62) strike = 'Severe Mishit';
  else if (absImpact > 0.46) strike = impactOffset > 0 ? 'Toe' : 'Heel';
  else if (tempoRatio < 1.15) strike = 'Fat';
  else if (tempoRatio > 7) strike = 'Thin';
  else if (path.maxBackswingPixels < 45) strike = 'Topped';
  else if (path.straightness > 0.92 && tempoScore > 0.82 && absImpact < 0.16) strike = 'Pure';
  else if (path.straightness < 0.72 || overswingRisk > 0.55 || tempoPenalty > 0.3)
    strike = 'Slight Mishit';
  const strikeScore =
    strike === 'Pure'
      ? 1.04
      : strike === 'Solid'
        ? 0.98
        : strike === 'Slight Mishit'
          ? 0.9
          : strike === 'Thin'
            ? 0.82
            : strike === 'Fat'
              ? 0.7
              : strike === 'Topped'
                ? 0.45
                : strike === 'Severe Mishit'
                  ? 0.35
                  : 0.78;
  const quality = THREE.MathUtils.clamp(
    strikeScore * (0.78 + path.straightness * 0.18) * (0.75 + tempoScore * 0.25) -
      overswingRisk * 0.08,
    0.3,
    1.05,
  );
  return {
    power,
    overswingRisk,
    backswingDuration,
    downswingDuration,
    tempoRatio,
    tempoScore,
    pathDegrees,
    faceDegrees,
    faceToPathDegrees,
    impactOffset,
    followThroughDegrees,
    strike,
    quality,
  };
}
